import { useAtomValue } from 'jotai';
import React, {
	CSSProperties,
	ForwardedRef,
	ReactNode,
	useContext,
	useMemo,
} from 'react';
import { rbcLocalizerAtom } from '../store/core';
import { CalendarSubComponentsContext } from './ResaCalendar';
import classNames from 'classnames';

const HighlightOpenHours = ({
	innerRef,
	...props
}: {
	slotMetrics: any;
	date: Date;
	children: React.ReactNode;
	className: string;
	style: CSSProperties;
	innerRef?: ForwardedRef<HTMLDivElement>;
}) => {
	const localizer = useAtomValue(rbcLocalizerAtom);
	const { openHours, min, max } = useContext(CalendarSubComponentsContext);
	const totalMinutes = localizer.getTotalMin(min, max);
	const percentPerMinute = 100 / totalMinutes;
	delete props.slotMetrics;

	const areas = useMemo(() => {
		const dayOpenHours = openHours.filter((e) =>
			localizer.eq(e.start, props.date, 'day'),
		);
		if (openHours.length > 0 && dayOpenHours.length == 0)
			return [
				{
					start: localizer.startOf(props.date, 'day'),
					end: localizer.endOf(props.date, 'day'),
					type: 'closed',
				} as const,
			];
		return dayOpenHours.flatMap((e, i, a) => {
			const ret = [] as ({ start: Date; end: Date } & (
				| { type: 'closed' }
				| { type: 'open'; label?: ReactNode }
			))[];
			if (i == 0)
				ret.push({
					start: localizer.startOf(e.start, 'day'),
					end: e.start,
					type: 'closed',
				});
			ret.push({
				...e,
				type: 'open',
			});
			ret.push({
				start: e.end,
				end: i >= a.length - 1 ? localizer.endOf(e.end, 'day') : a[i + 1].start,
				type: 'closed',
			});

			return ret;
		});
	}, [openHours, props.date]);

	return (
		<div
			ref={innerRef}
			{...props}
			className={classNames(props.className, 'rbc-highlighted-event')}
		>
			<div className="rbc-background-highlight">
				{areas.map((e, i) => {
					return (
						<div
							key={i}
							className={e.type}
							style={{
								top: `${localizer.getTotalMin(min, localizer.merge(new Date(), e.start)!) * percentPerMinute}%`,
								height: `${localizer.getTotalMin(localizer.merge(new Date(), e.start)!, localizer.merge(new Date(), e.end)!) * percentPerMinute}%`,
							}}
						>
							{e.type == 'open' && e.label && (
								<div className="label">{e.label}</div>
							)}
						</div>
					);
				})}
			</div>
			{props.children}
		</div>
	);
};
export const DayColumnWrapper = React.forwardRef(
	(props: any, ref: ForwardedRef<unknown>) => (
		<HighlightOpenHours {...props} innerRef={ref} />
	),
);
